import { autoId } from "@/data/ids";
import { useStudioStream } from "@/data/studio-react/useStudioStream";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { UserPlusIcon } from "@heroicons/react/24/solid";
import { Field, Form, Formik } from "formik";
import { Fragment } from "react";
import * as Yup from "yup";
import { Button } from "../Buttons";
import AddressField from "../clients/fields/AddressField";
import InputField from "../clients/fields/InputField";
import { PhoneNumberInput } from "../form/PhoneInput/PhoneNumberInput";

// Google libphonenumber
const PhoneNumberUtil = require("google-libphonenumber").PhoneNumberUtil;
const phoneUtil = PhoneNumberUtil.getInstance();

const validationSchema = Yup.object()
  .shape({
    id: Yup.string(),
    displayName: Yup.string(),
    email: Yup.string()
      .nullable()
      .test("email", "Invalid email format", function (value) {
        return !value || Yup.string().email().isValidSync(value);
      }),
    phoneNumber: Yup.string()
      .nullable()
      .test("phone", "Invalid phone number", function (value) {
        if (!value) return true; // Skip validation if empty

        try {
          // Parse as international number (should already be in international format from PhoneNumberInput)
          const number = phoneUtil.parseAndKeepRawInput(value);
          let isValid = phoneUtil.isValidNumber(number);

          return isValid;
        } catch (error) {
          return false;
        }
      }),
    landlineNumber: Yup.string()
      .nullable()
      .test("phone", "Invalid phone number", function (value) {
        if (!value) return true;

        try {
          const number = phoneUtil.parseAndKeepRawInput(value);
          let isValid = phoneUtil.isValidNumber(number);

          return isValid;
        } catch (error) {
          return false;
        }
      }),
    address1: Yup.string(),
    address2: Yup.string(),
    city: Yup.string(),
    province: Yup.string(),
    zip: Yup.string(),
    country: Yup.string().nullable(),
    company: Yup.string(),
    coordinates: Yup.object().nullable(),
    createdAt: Yup.date(),
    updatedAt: Yup.date(),
    updatedBy: Yup.string(),
    marteyeUiId: Yup.string(),
    notes: Yup.string(),
    accountEmails: Yup.boolean(),
    marketingEmails: Yup.boolean(),
  })
  .test({
    name: "at-least-one-field",
    message:
      "Please fill out at least one field (name, email, phone number, landline number, or address)",
    test() {
      const { displayName, email, phoneNumber, landlineNumber, address1 } =
        this.parent ?? {};
      return Boolean(
        (displayName && displayName.trim()) ||
          (email && email.trim()) ||
          (phoneNumber && phoneNumber.trim()) ||
          (landlineNumber && landlineNumber.trim()) ||
          (address1 && address1.trim())
      );
    },
  });
export type ContactFormValues = Yup.InferType<typeof validationSchema>;

export function AddEditContactDialog({
  isOpen,
  setIsOpen,
  defaultValues,
  onSubmit,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  defaultValues?: ContactFormValues | null;
  onSubmit: (values: ContactFormValues) => Promise<void>;
}) {
  let market = useStudioStream("session:market");

  const initialValues = {
    id: defaultValues?.id || "",
    displayName: defaultValues?.displayName || "",
    email: defaultValues?.email || "",
    phoneNumber: defaultValues?.phoneNumber || "",
    landlineNumber: defaultValues?.landlineNumber || "",
    address1: defaultValues?.address1 || "",
    address2: defaultValues?.address2 || "",
    city: defaultValues?.city || "",
    province: defaultValues?.province || "",
    zip: defaultValues?.zip || "",
    country: defaultValues?.country || "GB-ENG",
    company: defaultValues?.company || "",
    coordinates: defaultValues?.coordinates || null,
    notes: defaultValues?.notes || "",
    marteyeUiId: defaultValues?.marteyeUiId || "",
    accountEmails: defaultValues?.accountEmails || false,
    marketingEmails: defaultValues?.marketingEmails || false,
  };

  async function _handleSubmit(values: typeof initialValues) {
    // Runtime guard – if validation somehow passed, double-check essential fields
    const hasContent = Boolean(
      (values.displayName && values.displayName.trim()) ||
        (values.email && values.email.trim()) ||
        (values.phoneNumber && values.phoneNumber.trim()) ||
        (values.landlineNumber && values.landlineNumber.trim()) ||
        (values.address1 && values.address1.trim())
    );

    if (!hasContent) {
      // Should never happen because Yup validation blocks it, but extra safety
      const { toast } = await import("sonner");
      toast.error(
        "Please fill out at least one of name, email, phone number, landline number, or address"
      );
      return;
    }

    try {
      let id = values.id || autoId();
      values.id = id;
      await onSubmit(values);
    } catch (error) {}
  }

  const onClose = () => {
    setIsOpen(false);
  };

  let isEditing = !!defaultValues?.id;

  return (
    <>
      <Button
        type="button"
        variant="primary"
        onClick={() => setIsOpen(true)}
        icon={<UserPlusIcon className="size-4" />}
      >
        Add Contact
      </Button>
      <Transition show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[100]"
          // initialFocus={cancelButtonRef}
          onClose={onClose}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={_handleSubmit}
                  >
                    {({ isSubmitting, isValid, errors, values }) => (
                      <Form>
                        <div className="px-4 pt-5 sm:p-6">
                          <div className="flex items-start justify-between">
                            <DialogTitle
                              as="h3"
                              className="inline-flex items-center gap-x-1.5 text-xl font-bold"
                            >
                              <UserPlusIcon className="size-5" />
                              {isEditing ? "Edit" : "Add"} Contact
                            </DialogTitle>
                            <div className="ml-3 flex h-7 items-center">
                              <button
                                type="button"
                                className="rounded-full bg-martEye-500 p-1 text-martEye-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-martEye-400 hover:bg-martEye-400"
                                onClick={onClose}
                              >
                                <span className="sr-only">Close</span>
                                <XMarkIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </button>
                            </div>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            Add a new contact to your team.
                          </p>

                          <div className="mt-4 flex flex-col gap-4">
                            <InputField
                              label="Name"
                              name="displayName"
                              type="text"
                              placeholder="Name"
                            />
                            <InputField
                              label="Email"
                              name="email"
                              type="email"
                              placeholder="Email"
                            />
                            <PhoneNumberInput
                              label="Phone Number"
                              name="phoneNumber"
                              placeholder="Phone Number"
                              defaultCountry="GB"
                            />
                            <PhoneNumberInput
                              label="Landline Number"
                              name="landlineNumber"
                              placeholder="Landline Number"
                              defaultCountry="GB"
                            />

                            <div className="flex flex-col gap-2 border rounded-md px-2 py-2">
                              <h3 className="text-sm font-medium text-gray-900">
                                Address
                              </h3>
                              <div className="w-full">
                                <AddressField
                                  name="address"
                                  label="Search Address"
                                  defaultCountry={
                                    market?.countryCode || "GB-ENG"
                                  }
                                  marketId={market?.id}
                                />
                              </div>
                            </div>

                            <div className="flex flex-col gap-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Notes
                              </label>
                              <Field
                                as="textarea"
                                name="notes"
                                placeholder="Add any notes about this contact..."
                                rows={2}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                              />
                            </div>

                            {values.email && (
                              <div className="flex flex-col gap-3 border rounded-md px-3 py-3">
                                <h3 className="text-sm font-medium text-gray-900">
                                  Email Preferences
                                </h3>

                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Field
                                      type="checkbox"
                                      name="accountEmails"
                                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                    />
                                    <label className="text-sm text-gray-700">
                                      Account emails
                                    </label>
                                    <div className="group relative">
                                      <button
                                        type="button"
                                        className="inline-flex items-center justify-center w-4 h-4 text-gray-400 hover:text-gray-600 rounded-full border border-gray-300 text-xs"
                                      >
                                        i
                                      </button>
                                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block w-64 p-2 text-xs text-white bg-gray-900 rounded shadow-lg z-10">
                                        If checked, this person will be CC'd on
                                        all invoice emails sent to the customer.
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2">
                                  <Field
                                    type="checkbox"
                                    name="marketingEmails"
                                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                  />
                                  <label className="text-sm text-gray-700">
                                    Marketing emails
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="sticky bottom-0 bg-grey-100 -mx-6 -mb-6 px-12 pb-6 pt-4 mt-4 rounded-b-lg">
                          <div className="flex flex-row gap-2 w-full justify-between">
                            <div>
                              <Button
                                type="button"
                                variant="secondary"
                                onClick={onClose}
                              >
                                Cancel
                              </Button>
                            </div>
                            <div className="flex flex-row gap-2">
                              <Button
                                type="submit"
                                variant="primary"
                                isDisabled={isSubmitting || !isValid}
                              >
                                {isEditing
                                  ? "Update Contact"
                                  : "Create Contact"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
